import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { User, ShieldCheck, Key, Flask, Lock, Eye, EyeSlash, Check, Warning } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { errText } from '../utils/errors';
import UserScheduleBlocksSection from '../components/UserScheduleBlocksSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const roleLabels = {
  admin: 'Admin', investigador_principal: 'Investigador Principal', investigador: 'Co-Investigador',
  monitor: 'Monitor', coordinador: 'Coordinador', data_manager: 'Data Manager',
  sondas: 'Sondas', reclutamiento: 'Reclutamiento', director_estudio: 'Director Estudio',
};

const studyRoleLabels = {
  director_estudio: 'Director Estudio', investigador_principal: 'Inv. Principal', investigador: 'Co-Investigador',
  monitor: 'Monitor', coordinador: 'Coordinador', data_manager: 'Data Manager',
  sondas: 'Sondas', reclutamiento: 'Reclutamiento',
};

const statusLabels = {
  borrador: 'Borrador', activo: 'Activo', finalizado: 'Finalizado',
  completado: 'Completado', cancelado: 'Cancelado',
};

const statusColors = {
  borrador: 'bg-stone-100 text-stone-600 border-stone-200',
  activo: 'bg-green-50 text-green-700 border-green-200',
  completado: 'bg-green-50 text-green-700 border-green-200',
  cancelado: 'bg-red-50 text-red-600 border-red-200',
};

export default function Profile() {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const [hasPin, setHasPin] = useState(false);
  const [myStudies, setMyStudies] = useState([]);
  const [loadingStudies, setLoadingStudies] = useState(true);

  // PIN form
  const [pinForm, setPinForm] = useState({ pin: '', confirm: '' });
  const [savingPin, setSavingPin] = useState(false);

  // Password form
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [savingPw, setSavingPw] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const lb = lang === 'es' ? {
    title: 'Mi Perfil',
    subtitle: 'Gestiona tu cuenta, PIN de firma y consulta tus estudios asignados',
    info_title: 'Informacion de la cuenta',
    name: 'Nombre', email: 'Email', roles: 'Roles del sistema', member_since: 'Miembro desde',
    pin_title: 'PIN de firma electronica',
    pin_desc: 'El PIN se usa para firmar cuestionarios, eliminaciones y documentos conforme a 21 CFR Part 11.',
    pin_status: 'Estado del PIN', pin_configured: 'Configurado', pin_not_configured: 'Sin configurar',
    new_pin: 'Nuevo PIN (4-6 digitos)', confirm_pin: 'Confirmar PIN',
    save_pin: 'Guardar PIN', pin_mismatch: 'Los PINs no coinciden',
    pin_saved: 'PIN configurado correctamente',
    pw_title: 'Cambiar contrasena',
    current_pw: 'Contrasena actual', new_pw: 'Nueva contrasena', confirm_pw: 'Confirmar nueva contrasena',
    change_pw: 'Cambiar contrasena', pw_mismatch: 'Las contrasenas no coinciden',
    pw_changed: 'Contrasena actualizada correctamente', pw_min: 'Minimo 6 caracteres',
    studies_title: 'Estudios asignados',
    studies_desc: 'Estudios donde estas asignado como personal.',
    no_studies: 'No estas asignado a ningun estudio.',
    your_role: 'Tu rol',
  } : {
    title: 'My Profile',
    subtitle: 'Manage your account, signature PIN, and view assigned studies',
    info_title: 'Account Information',
    name: 'Name', email: 'Email', roles: 'System Roles', member_since: 'Member since',
    pin_title: 'Electronic Signature PIN',
    pin_desc: 'The PIN is used to sign questionnaires, deletions, and documents per 21 CFR Part 11.',
    pin_status: 'PIN Status', pin_configured: 'Configured', pin_not_configured: 'Not configured',
    new_pin: 'New PIN (4-6 digits)', confirm_pin: 'Confirm PIN',
    save_pin: 'Save PIN', pin_mismatch: 'PINs do not match',
    pin_saved: 'PIN configured successfully',
    pw_title: 'Change Password',
    current_pw: 'Current password', new_pw: 'New password', confirm_pw: 'Confirm new password',
    change_pw: 'Change password', pw_mismatch: 'Passwords do not match',
    pw_changed: 'Password updated successfully', pw_min: 'Minimum 6 characters',
    studies_title: 'Assigned Studies',
    studies_desc: 'Studies where you are assigned as staff.',
    no_studies: 'You are not assigned to any study.',
    your_role: 'Your role',
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/auth/pin-status`, { withCredentials: true })
      .then(r => setHasPin(r.data.has_pin))
      .catch(() => {});
    axios.get(`${API_URL}/api/auth/me/studies`, { withCredentials: true })
      .then(r => setMyStudies(r.data))
      .catch(() => {})
      .finally(() => setLoadingStudies(false));
  }, []);

  const handleSavePin = async () => {
    if (pinForm.pin !== pinForm.confirm) {
      toast.error(lb.pin_mismatch);
      return;
    }
    if (!pinForm.pin || !pinForm.pin.match(/^\d{4,6}$/)) {
      toast.error(lang === 'es' ? 'El PIN debe ser numerico de 4 a 6 digitos' : 'PIN must be 4-6 digits');
      return;
    }
    setSavingPin(true);
    try {
      await axios.put(`${API_URL}/api/auth/pin`, { pin: pinForm.pin }, { withCredentials: true });
      toast.success(lb.pin_saved);
      setHasPin(true);
      setPinForm({ pin: '', confirm: '' });
    } catch (err) {
      toast.error(errText(err));
    }
    setSavingPin(false);
  };

  const handleChangePw = async () => {
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error(lb.pw_mismatch);
      return;
    }
    if (pwForm.new_password.length < 6) {
      toast.error(lb.pw_min);
      return;
    }
    setSavingPw(true);
    try {
      await axios.put(`${API_URL}/api/auth/me/password`, {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      }, { withCredentials: true });
      toast.success(lb.pw_changed);
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(errText(err));
    }
    setSavingPw(false);
  };

  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const createdAt = user?.created_at ? new Date(user.created_at).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '-';

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-heading font-semibold text-stone-900">{lb.title}</h1>
        <p className="text-sm text-stone-500 mt-1">{lb.subtitle}</p>
      </div>

      {/* Account Info */}
      <Card className="border-stone-200 rounded-sm shadow-none" data-testid="profile-info-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-12 w-12 rounded-full bg-[#1f3e80] flex items-center justify-center">
              <span className="text-white text-lg font-semibold">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-stone-900">{user?.name}</h2>
              <p className="text-sm text-stone-500">{user?.email}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">{lb.roles}</p>
              <div className="flex flex-wrap gap-1.5">
                {userRoles.map(r => (
                  <Badge key={r} className="bg-emerald-50 text-emerald-800 border-emerald-200 text-xs" variant="outline">
                    {roleLabels[r] || r}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">{lb.member_since}</p>
              <p className="text-sm text-stone-700">{createdAt}</p>
            </div>
            {user?.is_professional && (
              <div>
                <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">{lang === 'es' ? 'Especialidad' : 'Specialty'}</p>
                <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs" variant="outline">{user.specialty}</Badge>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* PIN Management */}
      <Card className="border-stone-200 rounded-sm shadow-none" data-testid="profile-pin-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <Key size={18} className="text-[#1f3e80]" />
            <h3 className="text-base font-semibold text-stone-900">{lb.pin_title}</h3>
          </div>
          <p className="text-xs text-stone-500 mb-4">{lb.pin_desc}</p>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs text-stone-500">{lb.pin_status}:</span>
            {hasPin ? (
              <Badge className="bg-green-50 text-green-700 border-green-200 text-xs gap-1" variant="outline" data-testid="pin-status-badge">
                <ShieldCheck size={12} /> {lb.pin_configured}
              </Badge>
            ) : (
              <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs gap-1" variant="outline" data-testid="pin-status-badge">
                <Warning size={12} /> {lb.pin_not_configured}
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{lb.new_pin}</Label>
              <Input
                data-testid="profile-pin-input"
                type="password"
                maxLength={6}
                value={pinForm.pin}
                onChange={e => setPinForm(p => ({ ...p, pin: e.target.value.replace(/\D/g, '') }))}
                placeholder="****"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{lb.confirm_pin}</Label>
              <Input
                data-testid="profile-pin-confirm"
                type="password"
                maxLength={6}
                value={pinForm.confirm}
                onChange={e => setPinForm(p => ({ ...p, confirm: e.target.value.replace(/\D/g, '') }))}
                placeholder="****"
              />
            </div>
          </div>
          <Button
            data-testid="profile-save-pin-btn"
            onClick={handleSavePin}
            disabled={savingPin || !pinForm.pin || !pinForm.confirm}
            className="mt-3 bg-[#1f3e80] hover:bg-[#2a4f8a] text-white text-xs rounded-sm gap-1.5"
            size="sm"
          >
            <Check size={14} /> {savingPin ? '...' : lb.save_pin}
          </Button>
        </CardContent>
      </Card>

      {/* Password Change */}
      <Card className="border-stone-200 rounded-sm shadow-none" data-testid="profile-password-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <Lock size={18} className="text-[#1f3e80]" />
            <h3 className="text-base font-semibold text-stone-900">{lb.pw_title}</h3>
          </div>
          <div className="space-y-3 mt-4">
            <div className="space-y-1.5">
              <Label className="text-xs">{lb.current_pw}</Label>
              <div className="relative">
                <Input
                  data-testid="profile-current-pw"
                  type={showCurrent ? 'text' : 'password'}
                  value={pwForm.current_password}
                  onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))}
                />
                <button type="button" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600" onClick={() => setShowCurrent(!showCurrent)}>
                  {showCurrent ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">{lb.new_pw}</Label>
                <div className="relative">
                  <Input
                    data-testid="profile-new-pw"
                    type={showNew ? 'text' : 'password'}
                    value={pwForm.new_password}
                    onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))}
                  />
                  <button type="button" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600" onClick={() => setShowNew(!showNew)}>
                    {showNew ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{lb.confirm_pw}</Label>
                <Input
                  data-testid="profile-confirm-pw"
                  type="password"
                  value={pwForm.confirm}
                  onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <Button
            data-testid="profile-change-pw-btn"
            onClick={handleChangePw}
            disabled={savingPw || !pwForm.current_password || !pwForm.new_password || !pwForm.confirm}
            className="mt-3 bg-[#1f3e80] hover:bg-[#2a4f8a] text-white text-xs rounded-sm gap-1.5"
            size="sm"
          >
            <Lock size={14} /> {savingPw ? '...' : lb.change_pw}
          </Button>
        </CardContent>
      </Card>

      {/* URS-F416d: Mis Bloqueos de Agenda (accesible sin permisos de Configuracion) */}
      <Card className="border-stone-200 rounded-sm shadow-none" data-testid="profile-blocks-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <Lock size={18} className="text-[#1f3e80]" />
            <h3 className="text-base font-semibold text-stone-900">
              {lang === 'es' ? 'Mis bloqueos de agenda' : 'My schedule blocks'}
            </h3>
          </div>
          <p className="text-xs text-stone-500 mb-4">
            {lang === 'es'
              ? 'Marca las franjas en que no estarás disponible (vacaciones, formación, reuniones...). Reclutamiento verá un aviso al intentar citar en tus huecos bloqueados. Si eres el único IP/CoIP del estudio, ese hueco quedará bloqueado para el estudio.'
              : 'Mark the slots you will be unavailable (vacation, training, meetings...). Recruitment will see a warning when scheduling on your blocked slots. If you are the only IP/CoIP of the study, that slot will be blocked for the study.'}
          </p>
          {user?.id ? (
            <UserScheduleBlocksSection userId={user.id} lang={lang} hideCard />
          ) : null}
        </CardContent>
      </Card>

      {/* Assigned Studies */}
      <Card className="border-stone-200 rounded-sm shadow-none" data-testid="profile-studies-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <Flask size={18} className="text-[#1f3e80]" />
            <h3 className="text-base font-semibold text-stone-900">{lb.studies_title}</h3>
          </div>
          <p className="text-xs text-stone-500 mb-4">{lb.studies_desc}</p>
          {loadingStudies ? (
            <div className="text-center py-6">
              <div className="h-6 w-6 border-2 border-[#1f3e80] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : myStudies.length === 0 ? (
            <div className="text-center py-8 bg-stone-50 rounded-sm border border-stone-100">
              <Flask size={32} className="mx-auto text-stone-300 mb-2" />
              <p className="text-sm text-stone-400">{lb.no_studies}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {myStudies.map(s => (
                <div
                  key={s.study_id}
                  className="flex items-center justify-between bg-white border border-stone-200 rounded-sm px-4 py-3 hover:border-stone-300 transition-colors"
                  data-testid={`profile-study-${s.study_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-stone-800">{s.code}</span>
                        <Badge variant="outline" className={`text-[10px] ${statusColors[s.status] || 'bg-stone-50 text-stone-600'}`}>
                          {statusLabels[s.status] || s.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-stone-500 mt-0.5">{s.title}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {s.roles_in_study.map(r => (
                      <Badge key={r} variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                        {studyRoleLabels[r] || r}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
